import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Chip from "@material-ui/core/Chip";
import SearchIcon from "@material-ui/icons/Search";
import { format, parseISO } from "date-fns";
import moment from "moment";
import ModernDatePicker from "../../components/ModernDatePicker";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

const UsersReport = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [count, setCount] = useState(0);

  const [initialDate, setInitialDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [finalDate, setFinalDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsersOptions(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
  }, []);

  const fetchReport = async (page = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get("/users-report", {
        params: {
          initialDate,
          finalDate,
          userId: JSON.stringify(selectedUsers.map(u => u.id)),
          pageNumber: page
        },
      });
      if (page === 1) {
        setSessions(data.sessions);
      } else {
        setSessions(prev => [...prev, ...data.sessions]);
      }
      setHasMore(data.hasMore);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setPageNumber(1);
    fetchReport(1);
  };

  const loadMore = () => {
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchReport(nextPage);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatório de Acessos ({count})</Title>
      </MainHeader>

      <Paper className={classes.filterContainer} variant="outlined">
        <ModernDatePicker
          label="Data Inicial"
          value={initialDate ? moment(initialDate).toDate() : null}
          onChange={(newValue) => setInitialDate(moment(newValue).format("YYYY-MM-DD"))}
        />
        <ModernDatePicker
          label="Data Final"
          value={finalDate ? moment(finalDate).toDate() : null}
          onChange={(newValue) => setFinalDate(moment(newValue).format("YYYY-MM-DD"))}
        />
        <Autocomplete
          multiple
          size="small"
          options={usersOptions}
          getOptionLabel={(option) => option.name}
          value={selectedUsers}
          onChange={(e, v) => setSelectedUsers(v)}
          style={{ width: 300 }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" label="Filtrar por Usuários" placeholder="Usuários" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.name}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          startIcon={<SearchIcon />}
          style={{
            backgroundColor: "#6517ab",
            color: "white",
          }}
        >
          Filtrar
        </Button>
      </Paper>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Usuário</TableCell>
              <TableCell align="center">Login em</TableCell>
              <TableCell align="center">Logout em</TableCell>
              <TableCell align="center">Duração</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.id}>
                <TableCell align="center">{session.user?.name || "Desconhecido"}</TableCell>
                <TableCell align="center">
                  {format(parseISO(session.loginAt), "dd/MM/yyyy HH:mm:ss")}
                </TableCell>
                <TableCell align="center">
                  {session.logoutAt ? format(parseISO(session.logoutAt), "dd/MM/yyyy HH:mm:ss") : "Sessão Ativa / Não finalizada"}
                </TableCell>
                <TableCell align="center">
                  {session.logoutAt ? (
                    (() => {
                      const diff = new Date(session.logoutAt) - new Date(session.loginAt);
                      const hours = Math.floor(diff / (1000 * 60 * 60));
                      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                      return `${hours}h ${minutes}m ${seconds}s`;
                    })()
                  ) : "-"}
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={4} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default UsersReport;
